/*
 * SBV cart resync after an over-stock 422, plus the stock cap record.
 *
 * Why this file exists: the Shrine vendor bundle (js.shrinetheme.com) treats
 * an HTTP 422 from a cart write as "nothing happened". Shopify does not
 * agree. When a rider asks for more units than are in stock, Shopify caps
 * the quantity, REALLY CHANGES the cart, and answers 422 with a body like
 * {"status":422,"message":"Only 10 items were added to your cart due to
 * availability.","description":"..."}. The vendor script shows its generic
 * error and never re-renders, so the drawer keeps showing the pre-add
 * subtotal and header count while the real cart holds more.
 *
 * What this does: wraps window.fetch, watches for a 422 on /cart/add,
 * /cart/change or /cart/update, then pulls the truth (/cart.js plus the
 * cart-drawer and cart-icon-bubble sections) and re-renders the drawer
 * through the theme's own cart-drawer.renderContents, the same call
 * assets/sbv-cart-propack.js already makes in production. Shopify's own
 * message is written into the drawer error slot AFTER the re-render,
 * because renderContents replaces the node that slot lives in.
 *
 * Second job (2026-09-17): record the cap Shopify just enforced so the note
 * on the line survives a refresh. No Liquid number matches Shopify's own
 * message (Liquid reports 25 on the bundle variant while the 422 caps the
 * line at 10), so the only trustworthy source is the cart itself, read back
 * after the 422. For every variant the failed request touched, the cart's
 * post-422 count for that variant IS the cap. It is written under the
 * variant id into sessionStorage['sbv-stock-caps'] as
 * { cap, message, ts, others }, where others is the rest of the cart at the
 * moment Shopify stated the cap.
 * assets/sbv-cart-note.js renders the note from that record.
 *
 * Third job (2026-09-17, plan sbv_cart_eviction_001): surface a cart write
 * that SILENTLY removes another line. Raising one bundle line's quantity can
 * exhaust a component two bundles share, and Shopify then answers HTTP 200,
 * with no message of any kind, having deleted the other line. Reproduced on
 * live: a Honda Motorcycle Tool Set (1) plus a Triumph Add-On Pouch (1) at
 * $478.00, pouch to quantity 10, and the $349.00 set is simply gone. The 422
 * work above cannot catch it, because there is no 422.
 * So this file keeps a baseline of the cart (keys, per-line quantities,
 * titles and order), compares it against the cart after every write, and when
 * a line the rider did not touch is absent or reduced it says so in the
 * existing error slot. Nothing is restored and no cause is claimed: we have
 * established only that an untouched line went down.
 *
 * Rules it keeps:
 *   - The original response is returned untouched, so the vendor script
 *     behaves exactly as before. This only adds the repaint it skips.
 *   - Bound once per page (window guard), so a drawer re-render can never
 *     double-wrap fetch.
 *   - Never throws. Every step sits in a try/catch or a promise catch. A
 *     failure in here must never break an add to cart. sessionStorage is
 *     touched only inside try/catch (Safari private mode throws on access).
 *   - THE CLONE IS TAKEN IN THE FIRST then-CALLBACK ON THE FETCH PROMISE,
 *     never later. The vendor bundle attaches its own handler to the same
 *     promise and reads the body, and a Response can only be cloned while
 *     its body is unread. One extra microtask of delay (waiting on anything
 *     at all before cloning) hands the read to the vendor first and makes
 *     clone() throw. Caught in a browser test on staging 2026-09-17: the
 *     drawer sat on the pre-422 quantity and subtotal because resync() never
 *     ran. A console-issued fetch hides this, because no vendor handler is
 *     attached to it. Only the real interface path proves this file works.
 *   - The repaint is unconditional. Reading the 422 message and parsing
 *     which variants the request touched are both best effort: they can
 *     enrich the resync, never block it. Both are bounded by a timer.
 *   - On the cart PAGE it reloads instead, so subtotal, footer and the
 *     free-shipping bar cannot fall out of sync. The cap is written from its
 *     own /cart.js read BEFORE that reload, never after, because the reload
 *     ends this script's life.
 *   - A cap never outlives the constraint, and never dies while the
 *     constraint is alive. Any successful (non-422) cart write, and every
 *     page load that starts with a record in hand, reconciles against a
 *     fresh /cart.js. A record survives only while the in-cart count still
 *     equals it AND the rest of the cart is unchanged. Below it the cap no
 *     longer binds (a reduced or removed line); above it the number is stale
 *     (a restock); a different rest-of-cart means a different set of shared
 *     components, which moves what is available for this variant without
 *     moving its own count. Any of the three retires the record, so a stale
 *     number, a stale max or a stale aria-disabled cannot survive.
 *   - Only a /cart.js read may expire a record. The DOM may not: on /cart
 *     the layout's own cart drawer holds a second, often stale, copy of
 *     every line. assets/sbv-cart-note.js therefore never writes storage.
 *   - ONE /cart.js read per BURST of cart writes, and one at page load. The
 *     eviction compare, the baseline promotion and the cap reconcile all feed
 *     off the same body, so adding the third job above never makes a second
 *     request. That read is the honest cost of this feature and it is stated
 *     as such. A rider action is often two writes (see the burst comment
 *     below), and it still costs one read.
 *   - The baseline comes from /cart.js and never from the markup. The markup
 *     cannot answer it: the drawer remove control is a data-index button with
 *     no line key (blocks/cart-drawer_items.liquid), and while the cart page
 *     anchor does carry a key in url_to_remove, no rendered quantity is a
 *     per-LINE quantity. data-cart-quantity is cart | item_count_for_variant,
 *     a per-VARIANT total across lines. On /cart the layout's drawer also
 *     renders a second, often stale, copy of every line.
 *   - The eviction compare resolves what the rider touched against the
 *     PRE-write cart, never the post-write one. A request names a line by a
 *     1-based number or a position, and after the write those numbers point
 *     at whatever moved up: resolving them late makes a rider's own deletion
 *     look like a line taken from him.
 *   - Diff first, promote second. Promoting the new cart to the baseline
 *     before the compare would measure the cart against itself, find nothing
 *     ever, and leave a dead feature that still passes every negative test.
 *   - All FOUR AJAX cart endpoints are watched, not three. /cart/clear is
 *     never compared, only re-baselined: see isCartClear for the false
 *     positive that taught us so, which the drawer's trash control on the
 *     last line reaches every time.
 */
(function () {
  'use strict';

  if (window.__sbvCartSyncBound) { return; }
  window.__sbvCartSyncBound = true;

  if (typeof window.fetch !== 'function') { return; }

  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var CAPS_KEY = 'sbv-stock-caps';
  var CAPS_EVENT = 'sbv:cart-caps-changed';

  // Marks our own sentence inside the error slot, so Shopify's message keeps
  // the slot and our sentence can be replaced or cleared on its own.
  var NOTE_ATTR = 'data-sbv-evicted';

  // Let the vendor script finish its own 422 handling first, then correct
  // the display. Without this gap its generic error can land on top of the
  // real message from Shopify.
  var RESYNC_DELAY = 200;

  // Hard ceilings so the repaint can never be stranded by a read that does
  // not answer. The gather is cheap: the 422 body is already buffered and
  // the request body was parsed before the request even went out.
  var GATHER_TIMEOUT = 300;

  // The reload watchdog is a last resort, not a deadline. It was 1500 ms for
  // one push and that was too tight: /cart.js on this store regularly takes
  // longer, so the watchdog reloaded first and the page came back with no
  // cap recorded and no note (browser test, staging, 2026-09-17). Five
  // seconds only ever fires on a genuinely hung request.
  var RELOAD_WATCHDOG = 5000;

  var nativeFetch = window.fetch;
  var pending = false;

  // The cart at the last QUIET POINT, in order, one entry per line, each
  // carrying key, variant_id, quantity and title. Read from /cart.js at page
  // load and again once every burst of writes has settled. Never built from
  // the DOM (see the header).
  var baseline = null;

  /* ---------- why the compare runs per BURST and not per write ----------
   *
   * A rider action is not one cart write. Two real examples from this store,
   * both found in the browser on staging 2026-09-17 producing a WRONG
   * message, which is the one outcome this feature may never produce:
   *
   *   /cart quantity minus: POST /cart/change {"line":"1","quantity":"9"}
   *   and then, immediately, the vendor's own POST /cart/update.js with
   *   {"updates":{}} , which names NOTHING. Per write, the second write
   *   inherits a baseline taken before the first one landed, sees the line
   *   the rider just reduced, and cannot know the rider reduced it. Result:
   *   "Triumph Add-On Pouch is no longer in your cart." on a plain minus.
   *
   *   Pro Pack upgrade: POST /cart/add.js then POST /cart/change.js removing
   *   the old line by key. Per write, the add's read can land after the
   *   remove and report the Tool Set gone, untouched by the add.
   *
   * So the only sound frame is quiet point to quiet point: pre is the cart
   * when nothing was in flight, post is the cart once nothing is in flight
   * again, and touched is the union of every variant named by every write in
   * between. A burst is closed by SETTLE_DELAY of quiet, which bridges a
   * chained write issued from the previous response's own callback.
   */
  var SETTLE_DELAY = 200;

  var inFlight = 0;           // cart writes whose response has not settled
  var settleTimer = null;     // the pending burst-closed read
  var readGen = 0;            // any new write invalidates a read in flight
  var burstTouched = {};      // every variant named by any write in the burst
  var burstRefs = [];         // outstanding ref parses for the burst
  var burstSkip = false;      // a /cart/clear happened: re-baseline, no compare
  var owedTitles = [];        // the sentence currently owed, so a later render
                              // of the drawer can put it back

  function requestUrl(input) {
    try {
      if (typeof input === 'string') { return input; }
      if (input && typeof input.url === 'string') { return input.url; }
      if (input) { return String(input); }
    } catch (e) { /* an unreadable request is simply not ours */ }
    return '';
  }

  function isCartWrite(url) {
    if (!url) { return false; }
    return url.indexOf('/cart/add') !== -1
      || url.indexOf('/cart/change') !== -1
      || url.indexOf('/cart/update') !== -1;
  }

  // POST /cart/clear is the fourth AJAX cart endpoint, and it is NOT a write
  // this feature compares: the rider asked for every line to go. It still has
  // to be SEEN, because a cart the wrapper never noticed emptying leaves a
  // baseline full of lines that the next write is then blamed for.
  //
  // Found in the browser on staging 2026-09-17, and it is not a rare path:
  // the drawer's trash control posts /cart/change for a line, but /cart/clear
  // when the line it removes is the LAST one. So "rider empties the cart,
  // rider adds something else" showed "Honda Pro Pack is no longer in your
  // cart." on the add. A clear therefore re-baselines and never diffs.
  function isCartClear(url) {
    if (!url) { return false; }
    return url.indexOf('/cart/clear') !== -1;
  }

  function isCartPage() {
    try {
      return !!document.getElementById('main-cart-items');
    } catch (e) {
      return false;
    }
  }

  // Shopify's own message. Unchanged contract: it assigns the slot and an
  // empty text changes nothing. It runs AFTER a renderContents, because the
  // slot lives inside the markup that call replaces.
  function setCartError(text) {
    if (!text) { return; }
    try {
      var node = document.getElementById('CartDrawer-CartErrors')
        || document.getElementById('cart-errors');
      if (node) { node.textContent = text; }
    } catch (e) { /* the message is a courtesy, never a blocker */ }
  }

  // Both surfaces at once. The drawer slot is blocks/cart-drawer_items.liquid
  // and the cart page slot is sections/main-cart-footer.liquid, which sits
  // OUTSIDE that section's .js-contents, so a quantity re-render does not
  // wipe it. On /cart the layout renders its own drawer copy as well, so both
  // ids exist there: the drawer one is out of sight and harmless, the footer
  // one is the visible surface. setCartError above takes the first it finds,
  // which is why Shopify's message and our sentence are written separately.
  function errorSlots() {
    var nodes = [];
    try {
      var drawerSlot = document.getElementById('CartDrawer-CartErrors');
      var pageSlot = document.getElementById('cart-errors');
      if (drawerSlot) { nodes.push(drawerSlot); }
      if (pageSlot && pageSlot !== drawerSlot) { nodes.push(pageSlot); }
    } catch (e) { /* no slot, no message, no error either */ }
    return nodes;
  }

  // The explicit clear. Called from EVERY compare that finds nothing, not
  // only when a sentence is on screen: on a successful write the drawer is
  // repainted by the vendor, but nothing guarantees a repaint on every path,
  // and without this an old sentence would sit there after the cart moved on.
  function clearEviction() {
    try {
      var nodes = errorSlots();
      for (var i = 0; i < nodes.length; i++) {
        var marked = nodes[i].querySelectorAll('[' + NOTE_ATTR + ']');
        for (var j = 0; j < marked.length; j++) {
          if (marked[j].parentNode) { marked[j].parentNode.removeChild(marked[j]); }
        }
      }
    } catch (e) { /* the clear is cosmetic too */ }
  }

  // APPEND, never overwrite. Shopify's message keeps the slot; our sentence
  // is added after it as a marked child. Clearing first (not appending to our
  // own previous sentence) is what stops two consecutive evictions stacking
  // two sentences on a path that does not repaint.
  function showEviction(titles) {
    try {
      clearEviction();
      if (!titles || !titles.length) { return; }

      var parts = [];
      for (var t = 0; t < titles.length; t++) {
        // States only what we established: a line the rider did not touch is
        // no longer there. No cause asserted, no stock claim, no re-add.
        parts.push(titles[t] + ' is no longer in your cart.');
      }
      var text = parts.join(' ');

      var nodes = errorSlots();
      for (var i = 0; i < nodes.length; i++) {
        var existing = '';
        try { existing = String(nodes[i].textContent || '').replace(/^\s+|\s+$/g, ''); } catch (e) { existing = ''; }
        var span = document.createElement('span');
        span.setAttribute(NOTE_ATTR, '');
        span.className = 'sbv-cart-evicted';
        span.textContent = existing ? ' ' + text : text;
        nodes[i].appendChild(span);
      }
    } catch (e) { /* a missing message is a loss, never an error */ }
  }

  // No Accept: application/json header here. On a product URL Shopify
  // answers that header with the product JSON ({"product": ...}) instead of
  // the rendered sections, and the repaint then has nothing to paint
  // (found on staging 2026-09-17). Plain GET, no cache.
  function getJSON(url) {
    return nativeFetch.call(window, url, { cache: 'no-store' })
      .then(function (res) { return res.json(); })
      .catch(function () { return null; });
  }

  /* ---------- the cap record ---------- */

  function readCaps() {
    try {
      var raw = window.sessionStorage.getItem(CAPS_KEY);
      if (!raw) { return {}; }
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') { return {}; }
      return parsed;
    } catch (e) {
      return {};
    }
  }

  function writeCaps(caps) {
    try {
      var empty = true;
      for (var key in caps) {
        if (Object.prototype.hasOwnProperty.call(caps, key)) { empty = false; break; }
      }
      if (empty) {
        window.sessionStorage.removeItem(CAPS_KEY);
      } else {
        window.sessionStorage.setItem(CAPS_KEY, JSON.stringify(caps));
      }
    } catch (e) { /* no storage, no persistence, no error either */ }
  }

  function announceCaps() {
    try {
      var evt;
      if (typeof window.CustomEvent === 'function') {
        evt = new window.CustomEvent(CAPS_EVENT);
      } else {
        evt = document.createEvent('Event');
        evt.initEvent(CAPS_EVENT, true, true);
      }
      document.dispatchEvent(evt);
    } catch (e) { /* the renderer also watches the DOM, so this is a shortcut */ }
  }

  function capsAreEmpty() {
    var caps = readCaps();
    for (var key in caps) {
      if (Object.prototype.hasOwnProperty.call(caps, key)) { return false; }
    }
    return true;
  }

  /* ---------- which variants did the failed request touch ---------- */

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  // Variant ids are long integers. Line keys look like "44381134651613:cbf2b5".
  function looksLikeVariantId(value) {
    return /^\d{6,}$/.test(String(value));
  }

  // Returns the request body as a form-encoded string, whatever shape it
  // arrived in. A Request object has to be cloned and read asynchronously,
  // so everything here is promise-shaped.
  function requestBodyText(input, init) {
    try {
      var body = (init && init.body !== undefined && init.body !== null) ? init.body : null;

      if (body === null) {
        if (input && typeof input.clone === 'function' && typeof input.text === 'function') {
          return input.clone().text().catch(function () { return ''; });
        }
        return Promise.resolve('');
      }
      if (typeof body === 'string') { return Promise.resolve(body); }
      if (typeof body.toString === 'function' && typeof window.URLSearchParams === 'function'
        && body instanceof window.URLSearchParams) {
        return Promise.resolve(body.toString());
      }
      if (typeof body.forEach === 'function') {
        var parts = [];
        body.forEach(function (value, key) {
          try {
            parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(value)));
          } catch (e) { /* skip a value we cannot read */ }
        });
        return Promise.resolve(parts.join('&'));
      }
      return Promise.resolve('');
    } catch (e) {
      return Promise.resolve('');
    }
  }

  function decodeField(value) {
    try {
      return decodeURIComponent(String(value).replace(/\+/g, ' '));
    } catch (e) {
      return String(value);
    }
  }

  // A reference is whatever the request named: a variant id, a line key, a
  // 1-based line number, or a 0-based position in the cart's item list.
  function pushRef(refs, ref, qty) {
    if (ref === undefined || ref === null || ref === '') { return; }
    refs.push({ ref: String(ref), qty: (typeof qty === 'number' ? qty : toInt(qty)) });
  }

  function parseJsonBody(body) {
    var refs = [];
    var i;

    if (body.items && body.items.length) {
      for (i = 0; i < body.items.length; i++) {
        var entry = body.items[i];
        if (!entry) { continue; }
        pushRef(refs, entry.id, entry.quantity);
      }
      return refs;
    }

    if (body.updates) {
      if (body.updates.length !== undefined && typeof body.updates !== 'string') {
        for (i = 0; i < body.updates.length; i++) {
          refs.push({ position: i, qty: toInt(body.updates[i]) });
        }
      } else if (typeof body.updates === 'object') {
        for (var key in body.updates) {
          if (Object.prototype.hasOwnProperty.call(body.updates, key)) {
            pushRef(refs, key, body.updates[key]);
          }
        }
      }
      return refs;
    }

    if (body.id !== undefined && body.id !== null) {
      pushRef(refs, body.id, body.quantity);
      return refs;
    }

    if (body.line !== undefined && body.line !== null) {
      refs.push({ ref: String(body.line), qty: toInt(body.quantity) });
      return refs;
    }

    return refs;
  }

  function parseFormBody(text) {
    var refs = [];
    var fields = {};
    var positional = [];
    var itemFields = {};
    var pairs = String(text).split('&');
    var i;

    for (i = 0; i < pairs.length; i++) {
      if (!pairs[i]) { continue; }
      var split = pairs[i].indexOf('=');
      var rawKey = split === -1 ? pairs[i] : pairs[i].slice(0, split);
      var rawValue = split === -1 ? '' : pairs[i].slice(split + 1);
      var name = decodeField(rawKey);
      var value = decodeField(rawValue);

      var updateMatch = name.match(/^updates\[(.*)\]$/);
      if (updateMatch) {
        if (updateMatch[1] === '') {
          positional.push(toInt(value));
        } else {
          pushRef(refs, updateMatch[1], value);
        }
        continue;
      }

      var itemMatch = name.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (itemMatch) {
        if (!itemFields[itemMatch[1]]) { itemFields[itemMatch[1]] = {}; }
        itemFields[itemMatch[1]][itemMatch[2]] = value;
        continue;
      }

      fields[name] = value;
    }

    for (var idx in itemFields) {
      if (Object.prototype.hasOwnProperty.call(itemFields, idx)) {
        pushRef(refs, itemFields[idx].id, itemFields[idx].quantity);
      }
    }

    for (i = 0; i < positional.length; i++) {
      refs.push({ position: i, qty: positional[i] });
    }

    if (fields.id !== undefined) {
      pushRef(refs, fields.id, fields.quantity);
    } else if (fields.line !== undefined) {
      refs.push({ ref: String(fields.line), qty: toInt(fields.quantity) });
    }

    return refs;
  }

  function readTouched(input, init) {
    return requestBodyText(input, init).then(function (text) {
      var refs = [];
      try {
        if (text) {
          var trimmed = String(text).replace(/^\s+/, '');
          if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
            var parsed = null;
            try { parsed = JSON.parse(text); } catch (e) { parsed = null; }
            if (parsed && typeof parsed === 'object') {
              refs = parsed.length !== undefined
                ? parseJsonBody({ items: parsed })
                : parseJsonBody(parsed);
            }
          } else {
            refs = parseFormBody(text);
          }
        }
        if (!refs.length) {
          var url = requestUrl(input);
          var mark = url.indexOf('?');
          if (mark !== -1) { refs = parseFormBody(url.slice(mark + 1)); }
        }
      } catch (e) {
        refs = [];
      }
      return refs;
    }).catch(function () {
      return [];
    });
  }

  function countForVariant(cart, variantId) {
    var total = 0;
    try {
      if (!cart || !cart.items) { return 0; }
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === String(variantId)) {
          total += toInt(cart.items[i].quantity) || 0;
        }
      }
    } catch (e) {
      return 0;
    }
    return total;
  }

  // Turns the request's references into variant ids, using an item list for
  // the ones only the cart can answer (a line number, a line key, a position
  // in the update array). The list is either a cart's items (the cap record,
  // which reads the cap off the cart AFTER the 422) or our pre-write
  // baseline, whose entries carry the same key and variant_id fields on
  // purpose so this one resolver serves both.
  function resolveRefs(refs, items) {
    var resolved = [];
    var i;
    if (!items) { items = []; }

    for (i = 0; i < refs.length; i++) {
      var ref = refs[i];
      var variantId = null;

      if (ref.position !== undefined) {
        if (items[ref.position]) { variantId = items[ref.position].variant_id; }
      } else if (looksLikeVariantId(ref.ref)) {
        variantId = ref.ref;
      } else if (/^\d+$/.test(ref.ref)) {
        // A short integer on /cart/change is a 1-based line number.
        var line = toInt(ref.ref);
        if (line && items[line - 1]) { variantId = items[line - 1].variant_id; }
      } else {
        for (var j = 0; j < items.length; j++) {
          if (String(items[j].key) === ref.ref) { variantId = items[j].variant_id; break; }
        }
      }

      if (variantId === null || variantId === undefined) { continue; }
      resolved.push({ variantId: String(variantId), qty: ref.qty });
    }

    return resolved;
  }

  function resolveTouched(refs, cart) {
    return resolveRefs(refs, (cart && cart.items) ? cart.items : []);
  }

  /* ---------- the baseline, and the silent eviction compare ---------- */

  // A body counts as the whole cart only when it carries item_count AND
  // token AND an items list. /cart/add.js answers {"items":[...]} with
  // neither item_count nor token, so a write response can never be mistaken
  // for the cart. That is also why the baseline is promoted from our own
  // /cart.js read below: without that promotion, a cart built up during the
  // session (add the set, add the pouch, raise the pouch) would never have
  // the set in the baseline, and this plan's own reproduction would pass
  // silently while surfacing nothing.
  function isCartBody(body) {
    try {
      return !!(body && typeof body === 'object'
        && body.item_count !== undefined && body.item_count !== null
        && body.token
        && body.items && body.items.length !== undefined);
    } catch (e) {
      return false;
    }
  }

  function snapshotFrom(cart) {
    var out = [];
    try {
      var items = cart.items;
      for (var i = 0; i < items.length; i++) {
        var item = items[i] || {};
        out.push({
          key: item.key === undefined || item.key === null ? '' : String(item.key),
          variant_id: item.variant_id === undefined || item.variant_id === null
            ? '' : String(item.variant_id),
          quantity: toInt(item.quantity) || 0,
          // The title is taken from the PRE-write cart, so the sentence can
          // still name a line that no longer exists to be read from.
          title: String(item.product_title || item.title || '')
        });
      }
    } catch (e) {
      return out;
    }
    return out;
  }

  // Only ever called at a quiet point, with a body the read proved is a cart.
  function promoteBaseline(cart) {
    try {
      if (!isCartBody(cart)) { return; }
      baseline = snapshotFrom(cart);
    } catch (e) { /* an unpromoted baseline costs a message, never the cart */ }
  }

  // Which products went DOWN without the rider asking. Compared per variant,
  // not per line key, because a key is not stable across a removal and re-add
  // while a variant total is exactly the quantity the rider can see. `touched`
  // is the burst's accumulated set, already resolved against `pre`: resolving
  // a line number or a position against the POST-write cart is what turns a
  // rider's own deletion into an accusation.
  function evictedTitles(pre, cart, touched) {
    var titles = [];
    try {
      if (!pre || !pre.length || !isCartBody(cart)) { return titles; }
      if (!touched) { touched = {}; }

      var i;
      var order = [];
      var preTotals = {};
      var preTitles = {};
      for (i = 0; i < pre.length; i++) {
        var vid = pre[i].variant_id;
        if (!vid) { continue; }
        if (preTotals[vid] === undefined) {
          preTotals[vid] = 0;
          preTitles[vid] = pre[i].title;
          order.push(vid);
        }
        preTotals[vid] += pre[i].quantity;
      }

      var postTotals = {};
      for (i = 0; i < cart.items.length; i++) {
        var postId = String(cart.items[i].variant_id);
        postTotals[postId] = (postTotals[postId] || 0) + (toInt(cart.items[i].quantity) || 0);
      }

      for (i = 0; i < order.length; i++) {
        var id = order[i];
        if (touched[id]) { continue; }
        if ((postTotals[id] || 0) < preTotals[id] && preTitles[id]) {
          titles.push(preTitles[id]);
        }
      }
    } catch (e) {
      return [];
    }
    return titles;
  }

  // Paints whatever is currently owed. Called by the burst completion and
  // again by resync AFTER its renderContents, because that render replaces
  // the node the sentence lives in.
  function renderEviction() {
    try {
      showEviction(owedTitles);
    } catch (e) { /* never interfere with the cart */ }
  }

  /* ---------- the burst ---------- */

  function cancelSettleRead() {
    try {
      if (settleTimer) { window.clearTimeout(settleTimer); }
    } catch (e) { /* nothing to cancel */ }
    settleTimer = null;
  }

  function noteWriteIssued(clearing) {
    try {
      inFlight = inFlight + 1;
      if (clearing) { burstSkip = true; }
      cancelSettleRead();
      // Invalidates any read already in flight, including the page load read:
      // it can no longer describe a quiet cart.
      readGen = readGen + 1;
    } catch (e) { /* never interfere with the real request */ }
  }

  // Resolved against the baseline, which is the cart at the start of the
  // burst, because promotion only happens when the burst closes.
  function noteRefs(touchedPromise) {
    try {
      burstRefs.push(touchedPromise.then(function (refs) {
        try {
          var resolved = resolveRefs(refs || [], baseline || []);
          for (var i = 0; i < resolved.length; i++) {
            burstTouched[resolved[i].variantId] = true;
          }
        } catch (e) { /* an unparsed ref costs a message, never a wrong one */ }
        return refs;
      }).catch(function () { return []; }));
    } catch (e) { /* as above */ }
  }

  function noteWriteSettled(scheduleRead) {
    try {
      inFlight = inFlight - 1;
      if (inFlight < 0) { inFlight = 0; }
      if (inFlight === 0 && scheduleRead) {
        cancelSettleRead();
        settleTimer = window.setTimeout(runSettleRead, SETTLE_DELAY);
      }
    } catch (e) { /* as above */ }
  }

  // The ONE /cart.js read a burst of writes is allowed to make. It serves the
  // cap reconcile, the compare and the baseline promotion together.
  function runSettleRead() {
    settleTimer = null;
    try {
      if (inFlight > 0) { return; }
      var gen = readGen + 1;
      readGen = gen;
      var issuedAt = (new Date()).getTime();
      getJSON('/cart.js').then(function (cart) {
        closeBurst(cart, gen, issuedAt, false);
      });
    } catch (e) { /* never interfere with a successful cart write */ }
  }

  // gen guards against a write issued after this read went out. skipCaps is
  // set on the 422 path, where recordCaps has just written the cap this read
  // would otherwise be asked to judge.
  function closeBurst(cart, gen, issuedAt, skipCaps) {
    try {
      if (inFlight > 0 || gen !== readGen) { return; }
      // A read that did not come back as a cart decides nothing and resets
      // nothing: the burst simply stays open and the next one carries it.
      if (!isCartBody(cart)) { return; }

      var waiting = burstRefs.slice(0);
      Promise.all(waiting).then(function () {
        try {
          if (inFlight > 0 || gen !== readGen) { return; }
          if (!skipCaps) {
            try { reconcileCapsFromCart(cart, issuedAt); } catch (e) { /* caps are optional */ }
          }

          if (burstSkip) {
            // The rider emptied the cart himself. Nothing to compare.
            owedTitles = [];
            clearEviction();
          } else {
            // DIFF FIRST, PROMOTE SECOND. Promoting before the compare would
            // measure this cart against itself, find nothing ever, and leave
            // a dead feature that still passes every negative test.
            owedTitles = evictedTitles(baseline, cart, burstTouched);
            renderEviction();
          }

          promoteBaseline(cart);
          burstTouched = {};
          burstRefs = [];
          burstSkip = false;
        } catch (e) { /* leave the burst open rather than guess */ }
      }).catch(function () { /* as above */ });
    } catch (e) { /* as above */ }
  }

  // The cap Shopify enforced, written down. Only variants this request
  // actually asked too much of get a record: with one variant in the
  // request that is unambiguous, and with several (a full /cart/update) only
  // the ones whose requested quantity came back smaller than it was asked
  // for.
  function recordCaps(refs, cart, message) {
    try {
      if (!refs || !refs.length || !cart || !cart.items) { return; }
      // Resolved against the PRE-write baseline, never the post-write cart.
      // This used to pass `cart`, the cart as it is AFTER the write, which
      // contradicts the rule the header states for the eviction compare and is
      // wrong for exactly the same reason: a request names a line by a 1-based
      // number or by a position in the update array, and after the write those
      // numbers point at whatever line moved up. So a 422 that ALSO evicted a
      // line, which is reachable whenever two bundles share a component, would
      // write the cap under the WRONG variant. assets/sbv-cart-note.js would
      // then paint "1 available, the maximum for this order." on that line and
      // set max="1" plus aria-disabled on its plus button, and because neither
      // cart form carries novalidate a wrong max can block checkout outright.
      // A real variant id needs no list and still resolves; a ref only the
      // pre-write cart could have answered, on a page whose baseline has not
      // landed yet, is skipped instead of guessed. A missing note is always
      // acceptable, a wrong one is not.
      var resolved = resolveRefs(refs, baseline || []);
      if (!resolved.length) { return; }

      var caps = readCaps();
      var single = resolved.length === 1;
      var changed = false;

      for (var i = 0; i < resolved.length; i++) {
        var variantId = resolved[i].variantId;
        var count = countForVariant(cart, variantId);
        if (count <= 0) { continue; }
        var asked = resolved[i].qty;
        if (!single && !(typeof asked === 'number' && asked > count)) { continue; }
        caps[variantId] = {
          cap: count,
          message: message || '',
          ts: (new Date()).getTime(),
          // The rest of the cart at the moment Shopify stated this cap. A
          // cap is only true for one cart composition: components are shared
          // across DIFFERENT variants, so what else is in the cart changes
          // what is available for this one. See reconcileCapsFromCart.
          others: (toInt(cart.item_count) || 0) - count
        };
        changed = true;
      }

      if (changed) {
        writeCaps(caps);
        announceCaps();
      }
    } catch (e) { /* a missing note is a cosmetic loss, never an error */ }
  }

  // Whitmore ping 2, with one correction found in the browser. The ping
  // asked for a variant's record to be dropped on any successful write that
  // touched it. That is one step too aggressive: on /cart the vendor fires
  // its own successful POST /cart/update.js immediately after the 422, and
  // that write touches the very variant Shopify has just capped. Dropping on
  // touch therefore deleted a cap that was still being enforced, and the
  // note only survived the test because the page reload won the race
  // (staging, 2026-09-17).
  //
  // The rule that serves the same intent without the race is to reconcile
  // against the cart and let the values speak, which is also order
  // insensitive:
  //   count below the cap -> it no longer binds (a reduced or removed line).
  //     Drop it.
  //   count above the cap -> Shopify allowed more than it used to, so the
  //     number is stale (a restock). Drop it.
  //   rest of the cart changed -> see the composition note below. Drop it.
  //   count exactly the cap, same rest of cart -> Shopify is still enforcing
  //     it. Keep it.
  // A cap can therefore never state a number Shopify no longer enforces,
  // and never disappears while Shopify is still enforcing it.
  //
  // The /cart.js read used to belong to this function. It now arrives from
  // the caller, because the eviction compare and the baseline promotion need
  // the same body and a cart write may only make ONE read. issuedAt is
  // stamped by the caller BEFORE the read goes out, so this can tell a record
  // the snapshot describes from one written while it was in flight. Without
  // it a 422 landing during an earlier reconcile has its fresh record deleted
  // by a stale answer.
  function reconcileCapsFromCart(cart, issuedAt) {
    try {
      if (capsAreEmpty()) { return; }

      (function () {
        try {
          if (!cart || !cart.items) { return; }

          var caps = readCaps();
          var changed = false;

          for (var variantId in caps) {
            if (!Object.prototype.hasOwnProperty.call(caps, variantId)) { continue; }

            var entry = caps[variantId];
            var ts = toInt(entry && entry.ts);
            if (ts && ts > issuedAt) { continue; }

            var count = countForVariant(cart, variantId);
            var cap = toInt(entry && entry.cap);
            var others = (toInt(cart.item_count) || 0) - count;
            var wasOthers = toInt(entry && entry.others);

            // count below the cap: it no longer binds (a reduced or removed
            //   line).
            // count above the cap: Shopify allowed more, so the number is
            //   stale (a restock).
            // the rest of the cart changed: a cap is only true for one cart
            //   composition. Components are shared across DIFFERENT variant
            //   ids (parent spec: two brand Add-On Pouch lines both hold
            //   52527 under their own variant ids), so freeing or consuming
            //   one elsewhere moves what is available here WITHOUT moving
            //   this variant's own count. The record cannot be trusted and
            //   is retired. A missing note is always acceptable; a wrong
            //   number, with a max and an aria-disabled built on it, is not.
            if (!cap || count !== cap || (wasOthers !== null && others !== wasOthers)) {
              delete caps[variantId];
              changed = true;
            }
          }

          if (changed) { writeCaps(caps); }

          // Announced either way: the renderer never expires anything itself
          // any more, so this read is its cue to re-evaluate every line
          // against a record it can now trust.
          announceCaps();
        } catch (e) { /* leave the record as it was */ }
      })();
    } catch (e) { /* never interfere with a successful add to cart */ }
  }

  /* ---------- the repaint ---------- */

  function resync(description, refs) {
    // On the cart page the reload ends this script, so the record has to be
    // written first, off its own /cart.js read (Whitmore ping 1: resync()
    // never fetched the cart on this surface).
    //
    // On the 422 path this function owns closing the burst, because it makes
    // the read itself: the wrapper deliberately schedules no read for a 422.
    var gen = readGen;
    var issuedAt = (new Date()).getTime();

    // No eviction compare on this branch, and no promotion either: the
    // reload takes the page, the per-tab baseline and any sentence with it.
    // A 422 that also evicted a line therefore costs the message on /cart.
    // Logged as a limit rather than engineered around, because persisting the
    // sentence across a reload is a different feature.
    if (isCartPage()) {
      var reloaded = false;
      var reload = function () {
        if (reloaded) { return; }
        reloaded = true;
        try { window.location.reload(); } catch (e) { /* nothing left to try */ }
      };
      // The reload is the repaint on this surface, so it can never be left
      // waiting on a read. If /cart.js does not answer in time the page
      // still reloads, just without a recorded cap.
      window.setTimeout(reload, RELOAD_WATCHDOG);
      getJSON('/cart.js').then(function (cart) {
        recordCaps(refs, cart, description);
        reload();
      }).catch(reload);
      return;
    }

    var sectionsUrl = window.location.pathname + '?sections=' + SECTIONS;

    Promise.all([getJSON(sectionsUrl), getJSON('/cart.js')])
      .then(function (results) {
        var sectionsJson = results[0];
        var cartJson = results[1];

        recordCaps(refs, cartJson, description);

        if (!sectionsJson || !cartJson) { return; }

        var drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.renderContents !== 'function') { return; }

        // Same data shape the /cart/change.js response carries, which is what
        // renderContents expects: the cart JSON plus a sections object.
        drawer.renderContents(Object.assign({}, cartJson, { sections: sectionsJson }));

        if (cartJson.item_count > 0) {
          var drawerEl = document.querySelector('cart-drawer.drawer');
          if (drawerEl) { drawerEl.classList.remove('is-empty'); }
        }

        // After the re-render, never before: the error slot is inside the
        // markup renderContents has just replaced.
        setCartError(description);

        // The burst closes on THIS body, so the 422 path still makes exactly
        // one /cart.js read. skipCaps is true because recordCaps has just
        // written the cap from this very body.
        closeBurst(cartJson, gen, issuedAt, true);

        // The eviction sentence rides THIS post-render write, appended after
        // Shopify's message and never instead of it. Writing it before the
        // render would put it in a node that is about to be thrown away, and
        // the rider would be left with Shopify's message alone. closeBurst
        // above paints too, but it resolves the ref parses first, so this
        // second call is what actually lands after the render.
        window.setTimeout(renderEviction, 0);

        // The lines are new nodes, so the note renderer needs a nudge.
        announceCaps();
      })
      .catch(function () { /* leave the page exactly as the vendor left it */ });
  }

  // Shopify does not answer every cart endpoint in the same shape, and the
  // two that matter here disagree:
  //   /cart/add.js  -> {"status":422,"message":"Cart Error",
  //                     "description":"Only 10 items were added ..."}
  //   /cart/change  -> {"errors":"Only 10 items were added ..."}
  // The quantity input on both cart surfaces posts to /cart/change, so
  // reading only description and message left the drawer error slot blank on
  // the commonest path of all (browser test, staging, 2026-09-17). errors
  // can also arrive as a field-keyed object, so that shape is flattened too.
  function messageFromBody(body) {
    try {
      if (!body) { return ''; }
      if (typeof body === 'string') { return body; }
      if (body.description) { return String(body.description); }
      if (body.message) { return String(body.message); }

      var errors = body.errors;
      if (typeof errors === 'string') { return errors; }
      if (errors && typeof errors === 'object') {
        var parts = [];
        for (var key in errors) {
          if (!Object.prototype.hasOwnProperty.call(errors, key)) { continue; }
          var value = errors[key];
          parts.push(value && typeof value.join === 'function' ? value.join(' ') : String(value));
        }
        return parts.join(' ');
      }
    } catch (e) { /* an unreadable body just means no wording */ }
    return '';
  }

  // `snapshot` is a clone of the 422 response taken in the very first
  // then-callback on the fetch promise, before the vendor's own handler can
  // read the body. See the wrapper below for why that timing is the whole
  // ballgame. It may be null; the repaint runs either way.
  function watch(response, snapshot, touchedPromise) {
    var fired = false;

    // One entry point to the repaint, and it always gets used. Whatever the
    // body read or the variant parse do, they can only enrich this call,
    // never prevent it.
    var fire = function (description, refs) {
      if (fired) { return; }
      fired = true;
      window.setTimeout(function () {
        pending = false;
        resync(description || '', refs || []);
      }, RESYNC_DELAY);
    };

    try {
      if (!response) { return; }

      if (response.status !== 422) {
        // The silent eviction lands HERE, not below: Shopify answers a write
        // that deleted another line with a plain 200. Nothing to do in this
        // function, because the burst read the wrapper scheduled owns the
        // compare, the cap reconcile and the promotion together.
        return;
      }

      if (pending) { return; }
      pending = true;

      // Belt and braces: if either read stalls, the repaint still happens,
      // just without Shopify's wording or without a recorded cap.
      window.setTimeout(function () { fire('', []); }, GATHER_TIMEOUT);

      var readMessage = snapshot
        ? snapshot.json().then(messageFromBody).catch(function () { return ''; })
        : Promise.resolve('');

      var readRefs = touchedPromise
        ? touchedPromise.catch(function () { return []; })
        : Promise.resolve([]);

      Promise.all([readMessage, readRefs]).then(function (results) {
        fire(results[0], results[1]);
      }).catch(function () {
        fire('', []);
      });
    } catch (e) {
      // Even a throw in here must not cost the rider the repaint.
      try { fire('', []); } catch (e2) { pending = false; }
    }
  }

  window.fetch = function (input, init) {
    var url = requestUrl(input);
    var touched = null;
    var clearing = false;
    var watched = false;
    if (isCartWrite(url)) {
      // Nothing in here may throw into the caller's stack.
      try { touched = readTouched(input, init); } catch (e) { touched = Promise.resolve([]); }
      watched = true;
    } else if (isCartClear(url)) {
      clearing = true;
      watched = true;
    }
    if (watched) {
      // Opens or extends the burst BEFORE the request goes out, so the cart
      // the compare calls "before" is the cart at the last quiet point and
      // every ref this write names is resolved against that same frame.
      noteWriteIssued(clearing);
      if (touched) { noteRefs(touched); }
    }
    var result = nativeFetch.apply(window, arguments);

    if (watched) {
      try {
        result.then(function (response) {
          // A clear never reaches the clone or the compare below. It still
          // closes the burst, so the cart the rider just emptied is what the
          // next compare calls "before".
          if (clearing) {
            noteWriteSettled(true);
            return;
          }

          // CLONE HERE, IN THIS FIRST HOP, AND NOWHERE LATER.
          //
          // This handler is attached inside the wrapper, so it runs before
          // the vendor bundle's own handler on the same promise. The vendor
          // reads the body (response.json()) in its handler, and a Response
          // can only be cloned while its body is still unread. Waiting even
          // one extra microtask (for instance on the touched promise) before
          // cloning puts the vendor's read first and makes clone() throw
          // "body is already used". That regression shipped for one push on
          // 2026-09-17: the drawer kept showing the pre-422 quantity and
          // subtotal because resync() never ran at all.
          //
          // Only the message text depends on this clone. The repaint and the
          // cap recording must survive it failing.
          var snapshot = null;
          try {
            if (response && response.status === 422 && typeof response.clone === 'function') {
              snapshot = response.clone();
            }
          } catch (e) { snapshot = null; }

          // A 422 schedules no burst read: resync makes that read itself and
          // closes the burst on the same body, so the 422 path still costs
          // exactly one /cart.js.
          noteWriteSettled(!(response && response.status === 422));

          watch(response, snapshot, touched);
        }).catch(function () {
          // The request itself failed. The cart may or may not have moved, so
          // close the burst and let a real read decide.
          try { noteWriteSettled(true); } catch (e) { /* nothing left to do */ }
        });
      } catch (e) {
        // The handler could not even be attached. Do not leave the burst open
        // forever, or the baseline would never move again.
        try { noteWriteSettled(true); } catch (e2) { /* nothing left to do */ }
      }
    }

    // The caller gets the untouched original, always.
    return result;
  };

  // ONE read at page load, unconditional, and the only one. It does two jobs.
  //
  // Baseline: the eviction compare needs keys, per-line quantities, titles
  // and order, and the markup carries none of them (see the header). It has
  // to be in hand BEFORE the first write, so it cannot be fetched lazily.
  // This is the honest cost of the feature: one small JSON read per page
  // load, after DOMContentLoaded, on a deferred script, blocking nothing.
  //
  // Cap reconcile: a page load is not a cart write, so nothing above would
  // ever check a cap record carried across a reload or a navigation. The
  // renderer cannot do it either, since it only sees the DOM, which on /cart
  // includes a stale drawer copy of every line. That read used to be made
  // here separately and only when a record existed; it now shares this one.
  function readCartOnLoad() {
    try {
      var gen = readGen + 1;
      readGen = gen;
      var issuedAt = (new Date()).getTime();
      getJSON('/cart.js').then(function (cart) {
        try {
          // If the rider wrote to the cart while this read was in flight, the
          // answer is already history: noteWriteIssued moved readGen on, and
          // that burst's own read becomes the baseline instead.
          if (inFlight > 0 || gen !== readGen) { return; }
          try { reconcileCapsFromCart(cart, issuedAt); } catch (e) { /* caps are optional */ }
          promoteBaseline(cart);
        } catch (e) { /* a missing baseline costs a message, never the cart */ }
      });
    } catch (e) { /* as above */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', readCartOnLoad);
  } else {
    readCartOnLoad();
  }
})();
